import type { RecorderSnapshot, RecordedStep } from './scenarioRecorder';
import { RecorderEdgeCases, type EdgeCaseOutcome, recorderEdgeCases } from './recorderEdgeCases';

/**
 * UI<->gRPC orphan linking (spec §20 p.11,12). After a recording stops the step
 * timeline is analysed for two anomalies:
 *
 *  - **ui-without-grpc** — a user *command* that produced no Revit/gRPC call
 *    (the action did not reach the backend).
 *  - **grpc-without-ui** — a gRPC call with no preceding UI action that triggered
 *    it (a background / unattributed call).
 *
 * Each anomaly is recorded ON the step (`details.orphan`) so scenario.json shows
 * exactly which step is suspect, and reported through {@link RecorderEdgeCases}
 * so it reaches the diagnostics log and the UI — without ever throwing.
 *
 * The "is this UI step expected to call the backend?" decision is injectable so
 * new command shapes can be recognised without touching the linker.
 */

export type OrphanKind = 'ui-without-grpc' | 'grpc-without-ui';

/** Marker written onto an orphan step's details. */
export const ORPHAN_DETAIL_KEY = 'orphan';

/**
 * Default predicate for "this UI step should have triggered a backend call":
 * explicit commands (`command.*`). Plain clicks / input changes / navigation are
 * not expected to hit the backend and are never flagged.
 */
export function isBackendCommand(step: RecordedStep): boolean {
  return step.type === 'ui' && typeof step.action === 'string' && step.action.startsWith('command.');
}

export interface OrphanReport {
  uiWithoutGrpc: RecordedStep[];
  grpcWithoutUi: RecordedStep[];
}

export interface DetectOrphanOptions {
  /** Override the "expected to call the backend" predicate (extensibility). */
  isBackendCommand?: (step: RecordedStep) => boolean;
}

/**
 * Pure analysis of the ordered step timeline. A gRPC step is an orphan when no UI
 * step claims its correlation id; a command UI step is an orphan when it links to
 * no gRPC call. Never mutates its input.
 */
export function detectOrphanSteps(
  steps: readonly RecordedStep[],
  options: DetectOrphanOptions = {},
): OrphanReport {
  const isCommand = options.isBackendCommand ?? isBackendCommand;

  // Every correlation id a UI step claims (the UI->gRPC link recorded at capture).
  const linkedCorrelationIds = new Set<string>();
  for (const step of steps) {
    if (step.type === 'ui' && step.correlationIds) {
      for (const id of step.correlationIds) {
        linkedCorrelationIds.add(id);
      }
    }
  }

  const grpcWithoutUi: RecordedStep[] = [];
  for (const step of steps) {
    if (step.type !== 'grpc') {
      continue;
    }
    const correlationId = correlationIdOf(step);
    if (!correlationId || !linkedCorrelationIds.has(correlationId)) {
      grpcWithoutUi.push(step);
    }
  }

  const uiWithoutGrpc: RecordedStep[] = [];
  for (const step of steps) {
    if (isCommand(step) && (step.correlationIds?.length ?? 0) === 0) {
      uiWithoutGrpc.push(step);
    }
  }

  return { uiWithoutGrpc, grpcWithoutUi };
}

/** A gRPC step carries its correlation id in `target` (and in full-mode details). */
function correlationIdOf(step: RecordedStep): string | undefined {
  if (typeof step.target === 'string' && step.target.length > 0) {
    return step.target;
  }
  const fromDetails = step.details?.correlationId;
  return typeof fromDetails === 'string' ? fromDetails : undefined;
}

/** Stamp the orphan marker onto a step's details (mutates the step in place). */
export function markOrphanStep(step: RecordedStep, kind: OrphanKind): void {
  const details: Record<string, unknown> = { ...(step.details ?? {}) };
  details[ORPHAN_DETAIL_KEY] = kind;
  step.details = details;
}

/**
 * Analyse a (stopped) recording snapshot, mark every orphan step in place so the
 * gap is visible in scenario.json, and report each anomaly through the edge-case
 * mechanism (diagnostics log + UI). Returns the reported outcomes. Never throws.
 *
 * The snapshot's `steps` are mutated (they are already copies handed out by
 * {@link RecorderSnapshot}); pass a snapshot you are about to archive.
 */
export function linkOrphanSteps(
  snapshot: RecorderSnapshot,
  edge: RecorderEdgeCases = recorderEdgeCases,
  options: DetectOrphanOptions = {},
): EdgeCaseOutcome[] {
  const report = detectOrphanSteps(snapshot.steps, options);
  const outcomes: EdgeCaseOutcome[] = [];

  for (const step of report.grpcWithoutUi) {
    markOrphanStep(step, 'grpc-without-ui');
    outcomes.push(edge.report('grpc-without-ui', { index: step.index, method: step.action }));
  }
  for (const step of report.uiWithoutGrpc) {
    markOrphanStep(step, 'ui-without-grpc');
    outcomes.push(edge.report('ui-without-grpc', { index: step.index, action: step.action }));
  }

  return outcomes;
}
